import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';
import { Box, Typography, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { FiTarget } from 'react-icons/fi';

const Header = lazy(() => import('./components/Header'));
const Footer = lazy(() => import('./components/Footer'));
const ActivityTracker = lazy(() => import('./components/ActivityTracker'));
const PlanDeAhorro = lazy(() => import('./components/Plan_DeAhorro'));
const Help = lazy(() => import('./components/Help'));
const PlantillaByUser = lazy(() => import('./components/PlantillaByUser'));
const PlantillaCompartida = lazy(() => import('./components/compartirPlantilla'));
const Iniciar_sesion = lazy(() => import('./components/IniciarSesion'));
const Politics_and_privacy = lazy(() => import('./components/politics_and_privacy'));
const PageResetPassword = lazy(() => import('./components/PageResetPassword'));
const Contacto = lazy(() => import('./components/Contacto'));
const AlertaInicial = lazy(() => import('./components/AlertaInicial'));
const PrivacyNotice = lazy(() => import('./components/PrivacyNote'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const RealTimeGuard = lazy(() => import('./components/RealTimeGuard'));
const AboutMe = lazy(() => import('./components/AboutMe'));

const cookies = new Cookies();

/* ─── LOADING PANTALLA COMPLETA ──────────────────────────────────── */
const theme = createTheme({
  palette: { mode: 'dark' },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(29,158,117,0.4); }
          70%  { box-shadow: 0 0 0 10px rgba(29,158,117,0); }
          100% { box-shadow: 0 0 0 0 rgba(29,158,117,0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
      `,
    },
  },
});

const FullScreenLoading = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Box sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(29,158,117,0.12) 0%, transparent 60%), #080f14',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
    }}>
      <Box sx={{
        width: 72, height: 72,
        borderRadius: '20px',
        background: 'rgba(29,158,117,0.1)',
        border: '1px solid rgba(29,158,117,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'pulse-ring 2s infinite',
      }}>
        <FiTarget size={32} color="#1d9e75" />
      </Box>

      <Box sx={{ width: 200, height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 20, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: '40%',
          background: 'linear-gradient(90deg, transparent, #1d9e75, #5dcaa5, transparent)',
          borderRadius: 20,
          animation: 'shimmer 1.4s infinite linear',
          backgroundSize: '200% auto',
        }} />
      </Box>

      <Typography sx={{
        fontSize: 13, color: '#3d7a78',
        fontFamily: '"Outfit", sans-serif',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
      }}>
        Cargando…
      </Typography>
    </Box>
  </ThemeProvider>
);

/* ─── APP ─────────────────────────────────────────────────────────── */
function App() {
  const tokenUser = cookies.get('tokenUser');

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [plantillaAGenerar, setPlantillaAGenerar] = useState([]);
  const [message, setMessage] = useState('');

  // const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });
  const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

  useEffect(() => {
    const bootstrap = async () => {
      if (!tokenUser) { setLoading(false); return; }
      try {
        const userRes = await api.post('/getIdByUser', { tokenUser });
        const userData = {
          idUser: userRes.data.idUser,
          nombre: userRes.data.nombre,
          apellido: userRes.data.apellido,
          email: userRes.data.email,
          compartidosDe: userRes.data.compartidosDe,
          compartimosA: userRes.data.compartimosA,
          googleAvatar: userRes.data.googleAvatar,
          googleLogin: userRes.data.googleLogin,
          role: userRes.data.role || 'user',
          aprobado: userRes.data.aprobado ?? false,
          planAdquirido: userRes.data.planAdquirido ?? false,
          fechaRegistro: userRes.data.fechaRegistro,
        };
        // console.log('Bootstrap User Data:', userData);
        setUser(userData);

        const plantillasRes = await api.post('/getPlantillaByUser', {
          idUser: userData.idUser, tokenUser,
        });

        if (plantillasRes.data?.message) setMessage(plantillasRes.data.message);
        else setPlantillaAGenerar(plantillasRes.data);

      } catch (error) {
        console.error(error);
        const msg = error?.response?.data?.message;
        if (msg?.includes('JWT')) {
          cookies.remove('tokenUser', { path: '/' });
          Swal.fire({ title: msg, icon: 'error' });
          window.location.replace('/');
          return;
        }
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [tokenUser]);

  // ✅ mismo loading pantalla completa mientras carga el bootstrap
  if (loading) return <FullScreenLoading />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="app-container" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

          <Header
            idUser={user?.idUser}
            tokenUser={tokenUser}
            nombreUser={user?.nombre}
            apellidoUser={user?.apellido}
            emailUser={user?.email}
            googleAvatar={user?.googleAvatar}
            googleLogin={user?.googleLogin}
            role={user?.role}
            planAdquirido={user?.planAdquirido}
            fechaRegistro={user?.fechaRegistro}
          />

          {/* ✅ mismo loading pantalla completa como fallback de Suspense */}
          <Suspense fallback={<FullScreenLoading />}>
            <main className="content" style={{ flexGrow: 1 }}>
              {/* Rastreador de navegación */}
              <ActivityTracker idUser={user?.idUser} role={user?.role} />

              {/* Guardia en tiempo real (bloqueo/aprobación/suscripción) */}
              <RealTimeGuard
                idUser={user?.idUser}
                role={user?.role}
                aprobado={user?.aprobado}
                planAdquirido={user?.planAdquirido}
                fechaRegistro={user?.fechaRegistro}
              />

              <Routes>
                <Route index path="/" element={
                  <PlanDeAhorro idUser={user?.idUser} tokenUser={tokenUser} emailUser={user?.email} />
                } />

                <Route path="/iniciar_sesion" element={
                  <Iniciar_sesion setLoading={setLoading} />
                } />
                <Route path='/ayuda' element={<Help />} />
                <Route path='/sobre_mi' element={<AboutMe />} />

                <Route path="/compartir/:idPlantilla/:token" element={
                  <PlantillaCompartida
                    idUser={user?.idUser} tokenUser={tokenUser}
                    nombreUser={user?.nombre} apellidoUser={user?.apellido}
                    emailUser={user?.email} message={message}
                    compartimosA={user?.compartimosA} compartidosDe={user?.compartidosDe}
                  />
                } />

                <Route path="/politics_and_privacy" element={<Politics_and_privacy />} />

                <Route path="/new_password/:IdCliente" element={<PageResetPassword />} />

                <Route path="/contacto" element={
                  <Contacto idUser={user?.idUser} tokenUser={tokenUser} emailUser={user?.email} />
                } />

                <Route path="/mi_plantilla" element={
                  <PlantillaByUser
                    nombreUser={user?.nombre} apellidoUser={user?.apellido}
                    emailUser={user?.email} plantillaAGenerar={plantillaAGenerar}
                    tokenUser={tokenUser} message={message}
                    idUser={user?.idUser} compartimosA={user?.compartimosA}
                    compartidosDe={user?.compartidosDe} setLoading={setLoading}
                  />
                } />

                {/* Ruta /admin — solo accesible con role:'admin' */}
                <Route path="/admin" element={
                  user?.role === 'admin'
                    ? <AdminPanel idUser={user?.idUser} tokenUser={tokenUser} />
                    : <PlanDeAhorro idUser={user?.idUser} tokenUser={tokenUser} emailUser={user?.email} />
                } />
              </Routes>

              <AlertaInicial />
              <PrivacyNotice />
            </main>
          </Suspense>

          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;